/**
 * The launch locale set and the tag-normalisation function, in a module with NO
 * side effects.
 *
 * These used to live in `i18n/index.ts`, which calls `i18n.init(...)` at module
 * scope. That made `resolveLocale` unimportable from anywhere that must not boot
 * i18next — notably `shared/lib/formatters.ts`, which is pulled in by nearly every
 * unit test in the suite. Splitting the pure part out lets the formatters resolve
 * the active locale without dragging the detector, the lazy backend and eleven JSON
 * bundles into every test file. `i18n/index.ts` re-exports both symbols, so
 * existing `from '@/i18n'` imports are unaffected.
 */

/**
 * The TRANSLATION BUNDLES that exist on disk under `i18n/locales/`. Eleven, and this list
 * must equal that folder listing exactly — it is what `i18n.init`'s `supportedLngs` names
 * and what the lazy backend tries to fetch JSON for.
 *
 * Also the team member's UI-language set: a member picks the language their own app
 * chrome renders in, so an option with no bundle behind it would change nothing they can
 * see. Mirrored server-side by `PreferencesUpdateRequest.uiLocale`'s @Pattern.
 *
 * DO NOT add a region tag here to make it selectable somewhere. See CLIENT_LOCALES.
 *
 * `de-DE`, `nl-NL`, `pl-PL`, `cs-CZ` are the eighth to eleventh (RB-43 W0): `de-DE` starts as a
 * byte copy of `de-CH` (to be generated, W2); the other three start as English copies listed in
 * `PENDING_TRANSLATION`. `pl-PL`/`cs-CZ` need `_few`/`_many` plural forms beside every `_one`.
 *
 * `hu-HU` is the seventh (RB-12 C2): a real LANGUAGE bundle, not a region tag. Its JSON
 * files landed as byte-for-byte copies of en-US so every coverage guard holds and the app
 * runs "in Hungarian" showing English until the translations (RB-12 C3) replace them.
 */
export const SUPPORTED_LOCALES = [
  'en-US', 'de-CH', 'fr-CH', 'it-CH', 'es-ES', 'pt-BR', 'hu-HU',
  'de-DE', 'nl-NL', 'pl-PL', 'cs-CZ',
] as const
export type SupportedLocale = typeof SUPPORTED_LOCALES[number]

/**
 * The locales a CLIENT may be recorded as — `Client.locale` and the studio's default for
 * its clients, `StudioSettings.locale`. Sixteen: the eleven above plus five region-only tags.
 *
 * **Two lists because there are two jobs, and they are not the same job.** This one used
 * to be SUPPORTED_LOCALES doing both: naming the bundles that exist AND constraining what
 * a client's locale may be. Those constraints pull in opposite directions — bundles are
 * expensive and must stay few, while a client's REGION is free and drives real behaviour:
 * phone-country inference (`regionFromLocale`, as the last guess inside
 * `resolvePhoneCountry` when no `country` is recorded) and
 * date/number/currency formatting via `Intl`. A German client on `de-CH` had their phone
 * number parsed as Swiss.
 *
 * The five extra tags render through the SAME bundles — `resolveLocale()` maps them by
 * language prefix, so `de-AT` → the `de-DE` bundle (RB-43 D-02: Austria writes ß, Angebot, USt.). That is deliberate,
 * not a shortcut: it-IT vs it-CH is near-identical, and
 * duplicating 6,044 strings to encode that would raise the cost of every future string
 * from eleven translations to sixteen, forever.
 *
 * `pt-PT` is the one honest exception: it genuinely differs from `pt-BR` in vocabulary and
 * register (*ecrã/tela*, *ficheiro/arquivo*, *telemóvel/celular*), so it is the candidate
 * for a real bundle of its own later — if a Portuguese client ever actually appears. Until
 * then a pt-PT client reads Brazilian text with Portuguese dates and phone defaults, which
 * is a better trade than English.
 *
 * `en-CH` is the same mechanism pointed the other way: it is what a STUDIO picks for
 * ITSELF. A Zürich studio that works and writes in English had only `en-US` to say that,
 * and `en-US` is not a neutral "English" — it is a REGION, so the studio's own money came
 * out `CHF 3,200.00` and its dates `Sep 15, 2026`, on documents carrying a Swiss QR-bill.
 * `en-CH` renders through the `en-US` bundle (no new strings, nothing to translate) and
 * contributes CH: `CHF 3’200.00`, `15.09.2026`.
 *
 * Mirrored server-side by `com.studioflow.core.api.SupportedLocale` (sixteen constants,
 * each with a `bundleTag()`). Keep the two in sync. Adding a tag here means adding a
 * `locale.<tag>` key to ALL TEN locale files — `locale-coverage.test.ts` enforces it —
 * and an endonym in `shared/lib/localeEndonyms.ts`, which is a compile error otherwise.
 */
export const CLIENT_LOCALES = [
  ...SUPPORTED_LOCALES,
  'en-CH',
  'de-AT',
  'fr-FR',
  'it-IT',
  'pt-PT',
] as const
export type ClientLocale = typeof CLIENT_LOCALES[number]

/**
 * Normalise any incoming language tag to one of our BUNDLE locales.
 * Handles browser-reported tags (e.g. "de-DE", "fr", "en-GB") and
 * maps them to the closest supported locale. Exported so callers
 * (e.g. the language switcher) can validate user input consistently.
 *
 * This is bundle selection — it deliberately DISCARDS the region. Never format a date,
 * number or amount through its output: pass the full client tag to `Intl` instead, or a
 * `pt-PT` client gets Brazilian formatting and the region tags buy nothing.
 */
export function resolveLocale(input: string | null | undefined): SupportedLocale {
  if (!input) return 'en-US'

  // Exact match first (handles fully-qualified tags like "fr-CH")
  const exact = SUPPORTED_LOCALES.find(l => l.toLowerCase() === input.toLowerCase())
  if (exact) return exact

  // Region tags that read a sibling bundle rather than their language's default (RB-43 D-02).
  if (input.toLowerCase() === 'de-at') return 'de-DE'

  // Language-prefix match (e.g. "de" → "de-CH", "fr" → "fr-CH")
  const prefix = input.split('-')[0].toLowerCase()
  const LANGUAGE_DEFAULTS: Record<string, SupportedLocale> = {
    en: 'en-US',
    de: 'de-CH',
    fr: 'fr-CH',
    it: 'it-CH',
    es: 'es-ES',
    pt: 'pt-BR',
    hu: 'hu-HU',
    nl: 'nl-NL',
    pl: 'pl-PL',
    cs: 'cs-CZ',
  }
  return LANGUAGE_DEFAULTS[prefix] ?? 'en-US'
}
